
import { useState } from "react";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";

interface SaveConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (tendency: 'left' | 'right' | null, club?: string, notes?: string) => void;
  distance: number | null;
}

export const SaveConfirmDialog: React.FC<SaveConfirmDialogProps> = ({ 
  open, 
  onOpenChange, 
  onConfirm, 
  distance 
}) => {
  const { t } = useTranslation();
  const [selectedTendency, setSelectedTendency] = useState<'left' | 'right' | null>(null);
  const [showTendencySelection, setShowTendencySelection] = useState(false);
  const [club, setClub] = useState("");
  const [notes, setNotes] = useState("");

  const handleInitialConfirm = () => {
    setShowTendencySelection(true);
  };

  const handleTendencySelect = (tendency: 'left' | 'right' | null) => {
    setSelectedTendency(tendency);
    onConfirm(tendency, club.trim() || undefined, notes.trim() || undefined);
    // Reset state
    setShowTendencySelection(false);
    setSelectedTendency(null);
    setClub("");
    setNotes("");
  };

  const handleCancel = () => {
    setShowTendencySelection(false);
    setSelectedTendency(null);
    setClub("");
    setNotes("");
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleCancel}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("ballDistance.dialogs.saveShot.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {!showTendencySelection ? (
              <>
                {t("ballDistance.dialogs.saveShot.description")} 
                {t("ballDistance.dialogs.saveShot.distance")}: {distance?.toFixed(1)} {t("ballDistance.dialogs.saveShot.meters")}
              </>
            ) : (
              "Indiquez la direction de la balle et les informations complémentaires"
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        {!showTendencySelection ? (
          <>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="club">Club utilisé (optionnel)</Label>
                <Input
                  id="club"
                  placeholder="Ex: Driver, Fer 7, Wedge 56°"
                  value={club}
                  onChange={(e) => setClub(e.target.value.slice(0, 50))}
                  maxLength={50}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Observations (optionnel)</Label>
                <Textarea
                  id="notes"
                  placeholder="Ex: Vent de face, balle bien frappée, trajectoire haute..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                  maxLength={500}
                  rows={3}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground text-right">
                  {notes.length}/500
                </p>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancel}>
                {t("ballDistance.dialogs.saveShot.cancel")}
              </AlertDialogCancel>
              <Button onClick={handleInitialConfirm}>
                Continuer
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-20 hover:bg-primary/10"
                onClick={() => handleTendencySelect('left')}
              >
                <ArrowLeft className="h-6 w-6" />
                <span className="text-sm">Gauche</span>
              </Button>
              <Button
                variant="outline"
                className="flex flex-col items-center gap-2 h-20 hover:bg-primary/10"
                onClick={() => handleTendencySelect('right')}
              >
                <ArrowRight className="h-6 w-6" />
                <span className="text-sm">Droite</span>
              </Button>
            </div>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => handleTendencySelect(null)}
            >
              Passer (sans indiquer la direction)
            </Button>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCancel}>
                Annuler
              </AlertDialogCancel>
            </AlertDialogFooter>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
